import React, { useEffect, useState } from "react";
import { Delete, LockKeyhole, X } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

const PinCheck = ({
  correctPin,
  onSuccess,
  length = 6,
  title = "Enter Passcode",
  description = "Enter your PIN to continue",
}) => {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleNumber = (number) => {
    if (checking || pin.length >= length) return;

    setError(false);

    const newPin = `${pin}${number}`;
    setPin(newPin);

    if (newPin.length === length) {
      checkPin(newPin);
    }
  };

  const checkPin = (value) => {
    setChecking(true);

    // Small delay gives it a more natural PIN-lock feel
    setTimeout(() => {
      if (value === String(correctPin)) {
        setError(false);
        onSuccess?.();
        return;
      }

      setError(true);
      setChecking(false);

      setTimeout(() => {
        setPin("");
        setError(false);
      }, 700);
    }, 180);
  };

  const handleDelete = () => {
    if (checking) return;

    setError(false);
    setPin((prev) => prev.slice(0, -1));
  };

  const backgroundImage = "/caljn/caljn-odijn-tbcareer-livjn-miraelite.jpg";

  const navigate = useNavigate();

  const handleExit = () => {
    navigate("/");
  };

  return createPortal(
    <div
      className={`
      fixed inset-0 z-[2147483647]
      flex h-dvh w-screen
      items-center justify-center
      overflow-y-auto
      bg-cover bg-center bg-no-repeat
      px-4 py-6    ${!backgroundImage ? "bg-navy" : ""}
  `}
      style={
        backgroundImage
          ? {
              backgroundImage: `url("${backgroundImage}")`,
            }
          : undefined
      }
    >
      {/* Soft overlay - NOT BLACK */}
      <button
        type="button"
        onClick={handleExit}
        aria-label="Exit report access"
        className="
    absolute right-4 top-4 z-30
    flex items-center gap-2
    rounded-full
    border border-white/40
    bg-black/25
    px-4 py-2.5
    text-sm font-medium text-white
    shadow-lg backdrop-blur-md
    transition
    hover:bg-black/40
    active:scale-95
    sm:right-6 sm:top-6
  "
      >
        <X size={18} strokeWidth={2} />
        <span>Exit</span>
      </button>{" "}
      <div className="pointer-events-none absolute inset-0 bg-black/40" />
      {/* Extra depth */}
      <div
        className="
    pointer-events-none absolute inset-0
    bg-gradient-to-b
    from-black/25
    via-black/35
    to-black/60
  "
      />
      {/* Soft warm gradient */}
      <div
        className="
        pointer-events-none absolute inset-0
        bg-gradient-to-b
        from-white/5
        via-transparent
        to-navy/20
      "
      />
      <div className="relative z-10 flex w-full max-w-[420px] flex-col items-center">
        {/* Lock */}
        <div
          className="
          mb-7 flex h-10 w-10
          items-center justify-center
          rounded-full
          border border-white/40
          bg-white/20
          shadow-lg
          backdrop-blur-xl
        "
        >
          <LockKeyhole
            size={20}
            strokeWidth={1.7}
            className="text-white drop-shadow-md"
          />
        </div>

        {/* Heading */}
        <h1 className="text-center text-2xl font-semibold tracking-tight text-white drop-shadow-md md:text-2xl">
          {title}
        </h1>

        <p className="mt-2 text-center text-sm text-white/90 drop-shadow">
          {description}
        </p>

        {/* PIN dots */}
        <div
          className={`mt-6 flex items-center justify-center gap-4 ${
            error ? "animate-shake" : ""
          }`}
        >
          {Array.from({ length }).map((_, index) => {
            const filled = index < pin.length;

            return (
              <div
                key={index}
                className={`
                h-4 w-4 rounded-full border-2
                shadow-sm transition-all duration-200
                ${
                  filled
                    ? error
                      ? "scale-110 border-red-400 bg-red-400"
                      : "scale-110 border-white bg-white"
                    : "border-white bg-white/10"
                }
              `}
              />
            );
          })}
        </div>

        {/* Error */}
        <div className="mt-4 h-5">
          {error && (
            <p
              className="
              rounded-full bg-white/80
              px-3 py-1
              text-xs font-semibold text-red-500
              shadow-sm backdrop-blur-md
            "
            >
              Incorrect passcode. Try again.
            </p>
          )}
        </div>

        {/* Keypad */}
        <div className="mt-6 grid grid-cols-3 gap-x-5 gap-y-3 sm:gap-x-8">
          <PinButton number="1" onClick={handleNumber} />

          <PinButton number="2" letters="ABC" onClick={handleNumber} />

          <PinButton number="3" letters="DEF" onClick={handleNumber} />

          <PinButton number="4" letters="GHI" onClick={handleNumber} />

          <PinButton number="5" letters="JKL" onClick={handleNumber} />

          <PinButton number="6" letters="MNO" onClick={handleNumber} />

          <PinButton number="7" letters="PQRS" onClick={handleNumber} />

          <PinButton number="8" letters="TUV" onClick={handleNumber} />

          <PinButton number="9" letters="WXYZ" onClick={handleNumber} />

          <div className="h-[88px] w-[88px] sm:h-[96px] sm:w-[96px]" />

          <PinButton number="0" onClick={handleNumber} />

          <button
            type="button"
            disabled={!pin.length || checking}
            onClick={handleDelete}
            className="
            flex h-[88px] w-[88px]
            items-center justify-center
            rounded-full
            border border-white/20
            bg-white/10
            text-white
            backdrop-blur-xl
            transition

            hover:bg-white/25
            active:scale-90

            disabled:pointer-events-none
            disabled:opacity-30

            sm:h-[96px]
            sm:w-[96px]
          "
          >
            <Delete size={25} strokeWidth={1.6} className="drop-shadow-md" />
          </button>
        </div>

        {/* Footer */}
        <div className="mt-10 flex items-center gap-2.5 text-white/55">
          <div
            className="
      flex h-7 w-7 items-center justify-center
      rounded-full
      border border-white/15
      bg-white/10
      backdrop-blur-xl
    "
          >
            <LockKeyhole size={12} strokeWidth={1.8} />
          </div>

          <div className="flex flex-col">
            <span className="text-[11px] font-medium tracking-wide text-white/75">
              Secure access
            </span>

            <span className="text-[9px] tracking-[0.12em] text-white/35">
              PROTECTED BY CALJN
            </span>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

const PinButton = ({ number, letters, onClick }) => {
  return (
    <button
      type="button"
      onClick={() => onClick(number)}
      className="
        flex h-[78px] w-[78px]
        flex-col items-center justify-center
        rounded-full

        border border-white/50
        bg-white/15
        text-white

        shadow-[0_8px_30px_rgba(0,0,0,0.08),inset_0_1px_0_rgba(255,255,255,0.5)]

        backdrop-blur-md

        transition-all duration-150

        hover:border-white/70
        hover:bg-white/30

        active:scale-90
        active:bg-white/40

        sm:h-[96px]
        sm:w-[96px]
      "
    >
      <span
        className="
          text-[32px] font-light leading-none
          drop-shadow-md
          sm:text-[36px]
        "
      >
        {number}
      </span>

      {letters && (
        <span
          className="
            mt-1.5
            text-[9px] font-bold
            tracking-[0.3em]
            text-white
            drop-shadow-md
          "
        >
          {letters}
        </span>
      )}
    </button>
  );
};

export default PinCheck;
